import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { Logo } from "../components/Brand";
import { useApp, API } from "../AppContext";

export function ForgotPassword() {
  const { lang } = useApp();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      setSent(true); // intentionally always show success to prevent enumeration
    } finally {
      setLoading(false);
    }
  };

  const T = lang === "fr" ? {
    title: "Mot de passe oublié",
    sub: "Entrez votre adresse courriel — nous vous enverrons un lien pour réinitialiser votre mot de passe.",
    email: "Courriel",
    cta: "Envoyer le lien",
    sent_title: "Courriel envoyé",
    sent_sub: "Si un compte existe pour cette adresse, vous recevrez un lien de réinitialisation dans quelques instants. Le lien expire dans 1 heure.",
    back: "Retour à la connexion",
  } : {
    title: "Forgot password",
    sub: "Enter your email — we'll send you a link to reset your password.",
    email: "Email",
    cta: "Send reset link",
    sent_title: "Email sent",
    sent_sub: "If an account exists for this email, you'll receive a reset link shortly. The link expires in 1 hour.",
    back: "Back to login",
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 hero-grid-bg" style={{ background: "transparent" }} data-testid="forgot-password-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass-strong w-full max-w-md p-8">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs mb-6" style={{ color: "rgba(245,245,245,0.55)" }} data-testid="forgot-back">
          <ArrowLeft size={14} /> {T.back}
        </Link>
        <div className="mb-6"><Logo size="md" /></div>
        {sent ? (
          <div>
            <CheckCircle2 size={48} color="#4ADE80" className="mb-4" />
            <h1 className="font-heading text-2xl font-bold mb-3" style={{ color: "#F5F5F5" }}>{T.sent_title}</h1>
            <p className="text-sm" style={{ color: "rgba(245,245,245,0.7)" }} data-testid="forgot-sent-msg">{T.sent_sub}</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold mb-2" style={{ color: "#F5F5F5" }}>{T.title}</h1>
            <p className="text-sm mb-6" style={{ color: "rgba(245,245,245,0.6)" }}>{T.sub}</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2" color="rgba(245,245,245,0.4)" />
                <input type="email" required className="input-dark pl-11" placeholder={T.email}
                  value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
              </div>
              <button type="submit" disabled={loading} className="btn-red w-full justify-center" data-testid="forgot-submit">
                {loading ? "..." : T.cta}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function ResetPassword() {
  const { lang } = useApp();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) return setErr(lang === "fr" ? "Le mot de passe doit avoir au moins 6 caractères" : "Password must be at least 6 characters");
    if (pw !== pw2) return setErr(lang === "fr" ? "Les mots de passe ne correspondent pas" : "Passwords don't match");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setOk(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const T = lang === "fr" ? {
    title: "Nouveau mot de passe",
    sub: "Choisissez un nouveau mot de passe pour votre compte.",
    pw: "Nouveau mot de passe",
    pw2: "Confirmer le mot de passe",
    cta: "Réinitialiser",
    ok_title: "Mot de passe mis à jour",
    ok_sub: "Vous serez redirigé vers la page de connexion...",
    no_token: "Lien invalide ou expiré.",
  } : {
    title: "New password",
    sub: "Choose a new password for your account.",
    pw: "New password",
    pw2: "Confirm password",
    cta: "Reset",
    ok_title: "Password updated",
    ok_sub: "Redirecting to login...",
    no_token: "Invalid or expired link.",
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 hero-grid-bg" style={{ background: "transparent" }}>
        <div className="glass-strong p-8 max-w-md text-center">
          <h1 className="font-heading text-xl font-bold mb-2" style={{ color: "#F5F5F5" }}>{T.no_token}</h1>
          <Link to="/login" className="text-sm" style={{ color: "#D4AF37" }}>Retour</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 hero-grid-bg" style={{ background: "transparent" }} data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass-strong w-full max-w-md p-8">
        <div className="mb-6"><Logo size="md" /></div>
        {ok ? (
          <div>
            <CheckCircle2 size={48} color="#4ADE80" className="mb-4" />
            <h1 className="font-heading text-2xl font-bold mb-2" style={{ color: "#F5F5F5" }}>{T.ok_title}</h1>
            <p className="text-sm" style={{ color: "rgba(245,245,245,0.7)" }} data-testid="reset-ok-msg">{T.ok_sub}</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold mb-2" style={{ color: "#F5F5F5" }}>{T.title}</h1>
            <p className="text-sm mb-6" style={{ color: "rgba(245,245,245,0.6)" }}>{T.sub}</p>
            <form onSubmit={submit} className="space-y-4">
              <input type="password" required className="input-dark" placeholder={T.pw}
                value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw" />
              <input type="password" required className="input-dark" placeholder={T.pw2}
                value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw2" />
              {err && <div className="text-sm px-3 py-2 rounded-xl" style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.35)" }}>{err}</div>}
              <button type="submit" disabled={loading} className="btn-red w-full justify-center" data-testid="reset-submit">
                {loading ? "..." : T.cta}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
